import { useRef } from 'react';
import { pickDefaultModelResult } from '../../../../../../../../../../../common/model/data-point-pickers';
import { ImpactCategoryId } from '@upright/common/model/input/impacts/impact-categories';
import { Valence, ValueChain } from '../../../../../../../../../../../common/model/types';
import { useImpactChartContext, useImpactChartHeights, useImpactChartWidths } from '../../../../context';
import { StackedBar } from '../../../stacked-bar';
import { ImpactBarTooltip } from './tooltip';
import { ImpactRowBarProps } from './types';
import { useViewSetting } from '@upright/ui/common/generic/hooks';
import { useShowLeena2Uncertainty } from '@upright/ui/common/upright/model/output/scores/leena2-uncertainty/use-show-leena2-uncertainty';

export function ImpactRowBar(props: ImpactRowBarProps) {
  const { valence, index } = props;
  const expertMode = useViewSetting('netImpact.barAnnotations');
  const showUncertainty = useShowLeena2Uncertainty();
  const widths = useImpactChartWidths();
  const heights = useImpactChartHeights();
  const { desktopMode, data, getImpactClickUrl, onImpactClick, disableImpactClick, entityData, scoreReaders } =
    useImpactChartContext();
  const ref = useRef<SVGGElement>(null);

  if (!desktopMode) {
    return <></>;
  }

  const baseX = valence === Valence.NEGATIVE ? widths.title + widths.bars : widths.title + widths.bars + widths.middle;
  const seriesData = data[index];
  const entityRef = data[index];

  const entity = entityData?.find(datum => datum?.id === entityRef.id);
  const euPaiMetrics =
    props.impactCode === ImpactCategoryId.GHG_EMISSIONS && entity?.modelResults
      ? pickDefaultModelResult(entity.modelResults).euPaiMetrics
      : undefined;

  // Leena-2 Clef results only: the bar's 80% interval, read with the bar's own
  // score type (absolute vs relative) and unit scaling. Undefined — and the
  // bar unchanged — with the setting off or no interval for this category.
  const interval =
    showUncertainty && seriesData.impactIntervalsByTIVV
      ? scoreReaders[index].readIntervalWithScaling(seriesData.impactIntervalsByTIVV, props.impactCode, valence)
      : undefined;

  const refWithId = entityRef.id ? { name: entityRef.name, id: entityRef.id, type: entityRef.type } : undefined;

  // Per-series opt-out on top of the chart-wide `disableImpactClick` — see
  // `ImpactChartDataSeries.clickDisabled`.
  const clickDisabled = disableImpactClick || seriesData.clickDisabled === true;

  const getUrl = !clickDisabled
    ? (valueChain: ValueChain | undefined) => {
        if (getImpactClickUrl && refWithId !== undefined && seriesData.resultId !== undefined) {
          return getImpactClickUrl(valence, valueChain, props.impactCode, refWithId, seriesData.resultId);
        }
        return '';
      }
    : undefined;

  const onClick = (valueChain: ValueChain | undefined) => {
    if (clickDisabled) {
      return;
    }
    if (onImpactClick && refWithId !== undefined && seriesData.resultId !== undefined) {
      onImpactClick(valence, valueChain, props.impactCode, refWithId, seriesData.resultId);
    }
  };

  return (
    <g transform={`translate(${baseX}, 0)`}>
      <StackedBar
        index={index}
        data={seriesData.impactScoresByTIVV}
        impactCode={props.impactCode}
        maxWidth={widths.full}
        height={heights.impactRowBarHeight}
        valence={valence}
        getUrl={getUrl}
        onClick={onClick}
        svgId={props.svgId}
        color={seriesData.color}
        tooltipRef={ref}
        interval={interval}
      />
      {(expertMode || interval) && (
        <ImpactBarTooltip
          barReference={ref}
          data={seriesData.impactScoresByTIVV}
          impactCode={props.impactCode}
          valence={valence}
          euPaiMetrics={euPaiMetrics}
          showDetails={expertMode}
          interval={interval}
        />
      )}
    </g>
  );
}
